/**
 * KAN-773, KAN-790: редактор лестницы касаний - таблица шагов и кнопка
 * "Добавить касание".
 *
 * Значение - массив NudgeLadderStep, задержка и срок хранятся в часах. Когда
 * схема поля отдала каналы шага (`channelOptions`), у шага есть этап воронки
 * и выбор "что делаем" (канал шага); поля шага (рычаг, текст, срок) строка
 * показывает по признакам канала из схемы. Без `channelOptions` (бэк до KAN-790) редактор выглядит и
 * работает как раньше: номер, задержка, рычаг, до 5 шагов.
 *
 * Проверку лестницы делает форма-владелец (nudgeLadderError), текст ошибки
 * приходит через `error`. Строка шага - NudgeLadderRow.
 */
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  clampDelayHours,
  NUDGE_CHANNEL_DEFAULT,
  NUDGE_DELAY_MAX_H,
  NUDGE_DELAY_MIN_H,
  NUDGE_DUE_DEFAULT_H,
  NUDGE_LADDER_MAX_STEPS,
  NUDGE_LADDER_MAX_TOTAL,
  type NudgeChannelOption,
  type NudgeLadderStep,
  type NudgeLeverOption,
} from '@/lib/nudgeLadder';
import { NudgeLadderRow } from './NudgeLadderRow';

/** Шаг новой строки: сутки после самого позднего касания, в пределах 720 ч. */
const NEW_STEP_GAP_H = 24;

const NO_OPTIONS: ReadonlyArray<NudgeLeverOption> = [];

interface NudgeLadderEditorProps {
  /** Префикс id контролов, уникальный на странице. */
  idPrefix: string;
  value: NudgeLadderStep[];
  /** Рычаги касания (options поля схемы). */
  options: ReadonlyArray<NudgeLeverOption>;
  /** KAN-790: каналы шага (channel_options поля схемы); пусто - прежний вид. */
  channelOptions?: ReadonlyArray<NudgeChannelOption>;
  /** KAN-790: этапы воронки для шага, уже без запрещённых. */
  stageOptions?: ReadonlyArray<NudgeLeverOption>;
  onChange: (steps: NudgeLadderStep[]) => void;
  error?: string | null;
  disabled?: boolean;
  testId?: string;
}

export function NudgeLadderEditor({
  idPrefix,
  value,
  options,
  channelOptions = NO_OPTIONS,
  stageOptions = NO_OPTIONS,
  onChange,
  error,
  disabled = false,
  testId,
}: NudgeLadderEditorProps): JSX.Element {
  const staged = channelOptions.length > 0;
  const maxSteps = staged ? NUDGE_LADDER_MAX_TOTAL : NUDGE_LADDER_MAX_STEPS;

  // Stable React keys of the rows: a row keeps its local input state (chosen
  // unit, half-typed number) when another row is removed. If the owner replaces
  // the value with a list of another length, the keys are issued anew.
  const nextKey = useRef(0);
  const issueKeys = (count: number): number[] => Array.from({ length: count }, () => nextKey.current++);
  const [keys, setKeys] = useState<number[]>(() => issueKeys(value.length));
  const rowKeys = keys.length === value.length ? keys : value.map((_, i) => -1 - i);

  const patchStep = (index: number, patch: Partial<NudgeLadderStep>): void => {
    onChange(value.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  };

  const handleRemove = (index: number): void => {
    setKeys(rowKeys.filter((_, i) => i !== index));
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAdd = (): void => {
    if (value.length >= maxSteps) return;
    const latest = value.reduce((max, step) => Math.max(max, step.delay_h), 0);
    const delay = clampDelayHours(latest + NEW_STEP_GAP_H) ?? NEW_STEP_GAP_H;
    // The default channel; a schema without it starts from its first channel.
    const offersDefault = !staged || channelOptions.some((o) => o.value === NUDGE_CHANNEL_DEFAULT);
    setKeys([...rowKeys, ...issueKeys(1)]);
    onChange([
      ...value,
      {
        delay_h: delay,
        lever: options[0]?.value ?? '',
        channel: offersDefault ? NUDGE_CHANNEL_DEFAULT : channelOptions[0].value,
        stage: '',
        text: '',
        due_h: NUDGE_DUE_DEFAULT_H,
      },
    ]);
  };

  const canAdd = !disabled && value.length < maxSteps;

  return (
    <div data-testid={testId} className="space-y-2">
      {value.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="pb-1 pr-2 font-normal">Касание</th>
                {staged && <th className="pb-1 pr-2 font-normal">Этап</th>}
                <th className="pb-1 pr-2 font-normal">Задержка</th>
                {staged && <th className="pb-1 pr-2 font-normal">Что делаем</th>}
                <th className="pb-1 pr-2 font-normal">{staged ? 'Рычаг или задача' : 'Рычаг'}</th>
                <th className="pb-1" />
              </tr>
            </thead>
            <tbody>
              {value.map((step, index) => (
                <NudgeLadderRow
                  key={rowKeys[index]}
                  idPrefix={idPrefix}
                  testId={testId}
                  index={index}
                  step={step}
                  staged={staged}
                  leverOptions={options}
                  channelOptions={channelOptions}
                  stageOptions={stageOptions}
                  onChange={(patch) => patchStep(index, patch)}
                  onRemove={() => handleRemove(index)}
                  disabled={disabled}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid={testId ? `${testId}-add` : undefined}
        disabled={!canAdd}
        onClick={handleAdd}>
        Добавить касание
      </Button>
      <p className="text-xs text-muted-foreground">
        Задержка отсчитывается от предыдущего касания, от {NUDGE_DELAY_MIN_H} часа до{' '}
        {NUDGE_DELAY_MAX_H / 24} дней.{' '}
        {staged
          ? `Касание без этапа работает на любом этапе. На один этап не больше ${NUDGE_LADDER_MAX_STEPS} касаний вместе с общими, всего не больше ${NUDGE_LADDER_MAX_TOTAL}.`
          : `Не больше ${NUDGE_LADDER_MAX_STEPS} касаний.`}
      </p>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
